import { inject, Injectable } from "@angular/core";
import { Rating } from "../models/rating";
import { collection, Firestore, getDocs } from "@angular/fire/firestore";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import {Functions, httpsCallable} from "@angular/fire/functions";
import {InAppMessageAiTranslation} from "../models/in-app-message-ai-translation";


@Injectable({
  providedIn: 'root'
})
export class RatingService {

  private readonly firestore: Firestore = inject(Firestore);
  private readonly http: HttpClient = inject(HttpClient);
  private readonly firebaseFunctions = inject(Functions)



  async getIOSAppRatings(): Promise<Rating[]> {

    const collectionRef = collection(this.firestore, 'iosAppRatings');

    const snapshot = await getDocs(collectionRef);

    const result = snapshot.docs.map((result) => result.data() as Rating)

    this.sortRatingsDescending(result);

    return result.map((dta) => ({ "date": dta.date, "rating": this.roundNumber(dta.rating) }))
  }

  async getAndroidAppRatings(): Promise<Rating[]> {

    const collectionRef = collection(this.firestore, 'androidAppRatings');
    const snapshot = await getDocs(collectionRef);

    const result = snapshot.docs.map((result) => result.data() as Rating)

    this.sortRatingsDescending(result);

    return result.map((dta) => ({ "date": dta.date, "rating": this.roundNumber(dta.rating) }))
  }

  sortDates(dates: string[]) {
    dates.sort((a, b) => {
      const [monthA, yearA] = a.split('-').map(Number);
      const [monthB, yearB] = b.split('-').map(Number);

      if (yearA !== yearB) {
        return yearA - yearB;
      }
      return monthA - monthB;
    });
  }

  sortRatingsDescending(rating: Rating[]) {
    rating.sort((a, b) => {
      const [monthA, yearA] = a.date.split('-').map(Number);
      const [monthB, yearB] = b.date.split('-').map(Number);

      if (yearA !== yearB) {
        return yearB - yearA;
      }
      return monthB - monthA;
    });
  }

  roundNumber(value: number) {
    return Math.round(value * 10) / 10
  }

  getDropdownValues(iosRatings: Rating[], androidRatings: Rating[]) {
    return Array.from(new Set([...iosRatings.map((ios) => ios.date.split('-')[1]), ...androidRatings.map((android) => android.date.split('-')[1])]));
  }

  getXAxis(dropdownValue: string, xAxisData: string[]) {
    if (dropdownValue === 'all') {
      return xAxisData;
    } else {
      return xAxisData.filter((dta) => dta.includes(dropdownValue));
    }
  }

  getYAxis(xAxisDates: string[], map: { get: (arg0: string) => any; }) {
    return xAxisDates.map((date) => map.get(date) ?? 0)
  }


  async getAppStoreReviews(): Promise<any> {
    const url = 'https://europe-west3-mer-connect-dev-qa.cloudfunctions.net/getLatestAppStoreReviews';
    return firstValueFrom(this.http.get(url));
  }

  async getPlayStoreReviews(): Promise<any> {

    const translateMessageCallable = httpsCallable(this.firebaseFunctions, "getLatestPlayStoreReviews")
    const result = await translateMessageCallable()
    return result.data;

  }

}
